import React from 'react';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { useCallbackWithAnalytics } from '@atlaskit/analytics-next';
import { ButtonMenuItem, LinkMenuItem } from '@atlaskit/navigation-system';

import { ANALYTICS_CHANNEL } from '../../../common/constants';
import type { Item } from '../types';

/**
 * Component that renders either a menu button item or menu link item to be used in ObjectListFlyout.
 * Takes an item: Item prop.
 */
export function MenuItem({
	href,
	target,
	onClick,
	elemBefore,
	elemAfter,
	primaryLabel,
	secondaryLabel,
	tertiaryLabel,
}: Item) {
	const description = secondaryLabel
		? `${secondaryLabel}${tertiaryLabel ? ` • ${tertiaryLabel}` : ''}`
		: undefined;

	const commonProps = {
		elemBefore: elemBefore,
		elemAfter: elemAfter,
		description: description,
	};

	const onButtonClick = useCallbackWithAnalytics(
		(event: React.MouseEvent<HTMLButtonElement>) => onClick?.(event),
		{
			eventType: UI_EVENT_TYPE,
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'globalNavigation',
		},
		ANALYTICS_CHANNEL,
	);

	const onLinkClick = useCallbackWithAnalytics(
		(event: React.MouseEvent<HTMLAnchorElement>) => onClick?.(event),
		{
			eventType: UI_EVENT_TYPE,
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'globalNavigation',
		},
		ANALYTICS_CHANNEL,
	);

	if (href) {
		return (
			<LinkMenuItem {...commonProps} href={href} target={target} onClick={onLinkClick}>
				{primaryLabel}
			</LinkMenuItem>
		);
	}
	return (
		<ButtonMenuItem {...commonProps} onClick={onButtonClick}>
			{primaryLabel}
		</ButtonMenuItem>
	);
}
